import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";

// Autosize based on:
// https://www.stimulus-components.com/docs/stimulus-textarea-autogrow/

export default class extends Controller<HTMLInputElement> {
  declare onResize: EventListenerOrEventListenerObject; // eslint-disable-line no-undef
  declare autosizeValue: boolean;
  private intersectionObserver?: IntersectionObserver;

  static values = {
    autosize: { type: Boolean, default: false },
  };

  initialize(): void {
    this.autosize = this.autosize.bind(this);
  }
  connect(): void {
    if (this.autosizeValue) {
      this.enableAutosize();
    }
  }
  disconnect(): void {
    window.removeEventListener("resize", this.onResize);
    this.intersectionObserver?.disconnect();
  }
  submit() {
    // input elements have a `form` property pointing to their respective form
    // element. Calling `submit` on an input element assumes it is part of a form.
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/form
    this.element!.form!.requestSubmit();
  }

  autosize(): void {
    // Skip when hidden — scrollHeight is 0 and would collapse the textarea.
    if (!this.element.offsetParent) {
      return;
    }

    this.element.style.height = "auto"; // Force re-print before calculating the scrollHeight value.
    this.element.style.height = `${this.element.scrollHeight}px`;
  }

  enableAutosize(): void {
    this.element.style.overflow = "hidden";

    this.onResize = debounce(this.autosize, 150);

    this.autosize();

    this.element.addEventListener("input", this.autosize);
    window.addEventListener("resize", this.onResize);

    // If the element starts hidden (e.g. inside a collapsed <details>),
    // autosize once it becomes visible.
    if (!this.element.offsetParent) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          this.autosize();
          this.intersectionObserver?.disconnect();
        }
      });
      this.intersectionObserver.observe(this.element);
    }
  }
}
